(ns shared.core.components.recharts.stacked-bar-chart
  (:require [clojure.string :as s]
            [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.reactive :as re]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.components.recharts.tooltip :as tooltip]))

(style/add-css [".recharts-bar-rectangle:hover"
                {:filter "none !important"}]
               [".recharts-active-bar"
                {:filter "none !important"}]
               ;; Scoped, not global: the cursor is opt-in per chart (see `bar-chart`,
               ;; which shares this class) so bars that do nothing on click don't
               ;; advertise one.
               [".bar-chart-clickable .recharts-bar-rectangle"
                {:cursor "pointer"}])

(def default-colors
  ["#6DD9BD" "#A68CC5" "#64D2E8" "#F5B7B1" "#F9D99D" "#C4C4C4" "#2A9D8F" "#E9C46A"])

(defn calculate-stack-opacity
  "Calculates opacity for a bar based on its position in the stack.
   Bottom bar (position 0) = 1.0, top bar = 0.3"
  [position total-in-stack]
  (if (<= total-in-stack 1)
    1.0
    (- 1.0 (* (/ position (dec total-in-stack)) 0.7))))

(defn sum-values
  [values]
  (reduce (fn [acc {:keys [value]}]
            (if (number? value) (+ acc value) acc))
          0
          values))

(defn build-tooltip-row
  [{:keys [stack-name stack-name-colors key-positions total hovered-key label-transf-fn value-transf-fn show-percent?]
    :or {show-percent? true}
    :as context}
   {:keys [full-key display-name value]}]
  (when (and value (pos? value))
    (let [opacity (get-in key-positions [full-key :opacity] 1.0)
          base-color (or (get stack-name-colors full-key) (get stack-name-colors stack-name))
          indicator-color (util/hex->rgba base-color opacity)
          is-hovered (= full-key hovered-key)
          pct (if (pos? total) (* 100 (/ value total)) 0)]
      {:key (str full-key)
       :label (if label-transf-fn
                (label-transf-fn display-name)
                (util/->capitalize-words display-name))
       :value (cond
                value-transf-fn (value-transf-fn value context)
                (number? value) (util/readable-number value)
                :else value)
       :percent (when show-percent?
                  (str (.toFixed pct 1) "%"))
       :color base-color
       :highlight-color (util/hex->rgba base-color 0.12)
       :highlight? is-hovered
       :indicator {:color indicator-color
                   :shape :square}})))

(defn get-stack-name
  "Extracts the stack name from a key like 'phase.ordering' -> 'phase'"
  [key-str stack-separator]
  (first (s/split key-str stack-separator)))

(defn get-value-name
  "Extracts the value name from a key like 'phase.ordering' -> 'ordering'"
  [key-str stack-separator]
  (second (s/split key-str stack-separator)))

(defn get-stack-name-to-color-map
  "Creates a map of stack names to their assigned colors"
  [keys stack-separator colors]
  (let [unique-stack-names (distinct (map #(get-stack-name % stack-separator) keys))]
    (into {} (map-indexed (fn [idx stack-name]
                            [stack-name (or (get colors stack-name)
                                            (get colors (mod idx (count colors))))])
                          unique-stack-names))))

(defn get-color-for-key [key-str stack-name-colors stack-separator]
  (or (get stack-name-colors key-str)
      (get stack-name-colors (get-stack-name key-str stack-separator))))

(defn group-keys-by-stack
  [data-keys stack-separator]
  (group-by #(get-stack-name % stack-separator) data-keys))

(defn build-key-positions
  [keys-by-stack use-gradients]
  (reduce-kv
    (fn [acc _stack-name stack-keys]
      (let [stack-count (count stack-keys)]
        (reduce (fn [inner-acc [idx key-str]]
                  (assoc inner-acc key-str
                                   {:position idx
                                    :opacity (if use-gradients
                                               (calculate-stack-opacity idx stack-count)
                                               1)}))
                acc
                (map-indexed vector stack-keys))))
    {}
    keys-by-stack))

(defn bar-node?
  [node]
  (when (.-classList node)
    (or (.contains (.-classList node) "recharts-bar-rectangle")
        (.contains (.-classList node) "recharts-rectangle"))))

(defn click-target-bar?
  [target]
  (loop [node target
         depth 0]
    (cond
      (nil? node) false
      (>= depth 20) false
      (bar-node? node) true
      :else (recur (.-parentNode node) (inc depth)))))

(defn build-stack-values
  [stack-keys stack-separator data]
  (map (fn [key-str]
         {:full-key key-str
          :display-name (get-value-name (name key-str) stack-separator)
          :value (get data (keyword key-str))})
       stack-keys))

(defn estimate-chart-width
  [date-count stack-count bar-width]
  (let [group-width (+ stack-count 2)]
    (+ 100 (* date-count bar-width group-width))))

(defn custom-rotated-x-tick [props]
  (let [{:keys [x y angle payload]} (js->clj props :keywordize-keys true)
        value-str (:value payload)]
    (re/as-element
      [:g {:transform (str "translate(" x "," y ")"
                           (when angle
                             (str " rotate(" angle ")")))}
       [:text {:x 0
               :y 0
               :dy 16
               :text-anchor (cond
                              (pos? angle) "start"
                              (neg? angle) "end"
                              :else "middle")
               :font-style :italic
               :font-family style/roboto-font-family
               :fill (style/alc-color "Text/primary")
               :font-size 11}
        value-str]])))

(defn custom-stack-tooltip
  "Custom tooltip that shows only data for a specific stack"
  [{:keys [tooltip-state* mouse-position* stack-name-colors key-positions header-transf-fn label-transf-fn
           value-transf-fn show-percent? column-template show-double-click-message?]
    :or {show-percent? true}
    :as r}]
  (when-let [{:keys [stack-name values hovered-key]} @tooltip-state*]
    (let [{:keys [x y]} @mouse-position*
          total (sum-values values)
          context {:stack-name stack-name
                   :stack-name-colors stack-name-colors
                   :key-positions key-positions
                   :total total
                   :hovered-key hovered-key
                   :label-transf-fn label-transf-fn
                   :value-transf-fn value-transf-fn
                   :state @tooltip-state*
                   :show-percent? show-percent?}
          rows (->> values
                    (map #(build-tooltip-row context %))
                    (remove nil?)
                    vec)]
      [tooltip/tooltip-panel {:style-map (tooltip/floating-style x y {:tooltip-width 320})
                              :numeric-align :right
                              :column-template (or column-template "minmax(140px,1fr) 72px")
                              :header {:label (if header-transf-fn
                                                (header-transf-fn r)
                                                (util/->capitalize-words stack-name))
                                       :value nil
                                       :percent nil}
                              :rows rows
                              :show-double-click-message? show-double-click-message?}])))

(defn calculate-max-stacked-value
  "Calculates the maximum stacked value across all dates"
  [chart-data keys stack-separator]
  (let [;; Group keys by their stack name (stackId)
        keys-by-stack (group-by #(get-stack-name % stack-separator) keys)]
    (->> chart-data
         (map (fn [date-data]
                ;; For each date, calculate the max stack height
                (->> keys-by-stack
                     (map (fn [[_ key-list]]
                            ;; Sum all values for this stack name
                            (->> key-list
                                 (map #(or (get date-data %) (get date-data (keyword %) 0)))
                                 (apply +))))
                     (apply max 0))))
         (apply max 0))))

(defn stacked-bar-chart
  [_]
  (let [click-timer* (atom nil)
        tooltip-state* (re/ratom nil)
        mouse-position* (re/ratom {:x 0 :y 0})
        ;; When set, only this segment (series key) is fully opaque; others fade.
        selected-key* (re/ratom nil)
        ;; Track specific clicked bar (x/name) for outlining that one rectangle
        selected-datum* (re/ratom nil)]
    (fn [{:keys [chart-data data-keys stack-separator colors bar-size chart-height show-legend use-gradients
                 tooltip-header-transf-fn tooltip-label-transf-fn tooltip-value-transf-fn tooltip-show-percent? y-max
                 chart-container-style chart-style y-axis? chart-width tooltip-column-template x-axis-config
                 y-axis-config bar-config tooltip-config dynamic-margins? fill-width? point-width]
          :or {colors default-colors
               bar-size 20
               chart-height 350
               show-legend true
               use-gradients true
               tooltip-show-percent? true
               y-axis? true
               dynamic-margins? true}}]
      (let [stack-name-colors (if (map? colors)
                                colors
                                (get-stack-name-to-color-map data-keys stack-separator colors))
            keys-by-stack (group-keys-by-stack data-keys stack-separator)
            key-positions (build-key-positions keys-by-stack use-gradients)
            stack-count (count keys-by-stack)
            date-count (count chart-data)
            chart-width (or chart-width
                            (if point-width
                              (+ 100 (* date-count point-width))
                              (estimate-chart-width date-count stack-count bar-size)))
            max-value (calculate-max-stacked-value chart-data data-keys stack-separator)
            y-max (or y-max (Math/ceil (* max-value 1.05)))
            y-axis-width (when (and dynamic-margins? y-axis? (not (:width y-axis-config)))
                           (recharts.util/fit-y-axis-width [(util/readable-number (or y-max max-value 0))]))
            x-axis-height (when (and dynamic-margins? (not (:height x-axis-config)))
                            (recharts.util/fit-x-axis-height
                              (map #(or (:name %) (get % "name")) chart-data)
                              (:angle x-axis-config)))
            ;; What a bar click does: whatever the caller wired up, or the built-in
            ;; segment highlight — which needs several segments to have any effect.
            bars-clickable? (boolean (or (:on-click bar-config)
                                         (:on-double-click bar-config)
                                         (> (count data-keys) 1)))]
        [:div {:style (merge {:width "100%"
                              :display :flex
                              :flex-direction :column
                              :gap 12}
                             chart-container-style)}
         [recharts.util/scroll-frame {:fill-width? fill-width?
                                      :min-width (when fill-width? chart-width)
                                      :height chart-height
                                      :style chart-style
                                      :class (when bars-clickable? "bar-chart-clickable")
                                      :on-mouse-move (fn [e]
                                                       (reset! mouse-position* {:x (.-clientX e)
                                                                                :y (.-clientY e)}))
                                      :on-click (fn [e]
                                                  (when-not (click-target-bar? (.-target e))
                                                    (reset! selected-key* nil)
                                                    (reset! selected-datum* nil)))}
          [recharts.util/re-bar-chart (cond-> {:data (clj->js chart-data)
                                               :responsive true
                                               :height chart-height
                                               :margin {:top 20 :right 30 :left 20 :bottom 20}}
                                              (not fill-width?)
                                              (assoc :width chart-width))
           (when use-gradients
             [:defs
              (for [data-key data-keys]
                (let [base-color (get-color-for-key data-key stack-name-colors stack-separator)
                      light-color (util/lighten-hex base-color 0.3)
                      gradient-id (str "gradient-" (util/string->hash data-key))]
                  [:linearGradient {:key gradient-id
                                    :id gradient-id
                                    :x1 "0%"
                                    :y1 "0%"
                                    :x2 "100%"
                                    :y2 "0%"}
                   [:stop {:offset "0%"
                           :stop-color light-color
                           :stop-opacity 1}]
                   [:stop {:offset "100%"
                           :stop-color base-color
                           :stop-opacity 1}]]))])
           [recharts.util/re-cartesian-grid {:stroke-dasharray "3 3"
                                             :stroke (style/alc-color "lines/chart_grid")
                                             :vertical false}]
           [recharts.util/re-x-axis (merge {:dataKey "name"
                                            :tick custom-rotated-x-tick
                                            :height (or x-axis-height 80)
                                            :interval 0}
                                           x-axis-config)]
           (when y-axis?
             [recharts.util/re-y-axis (merge {:tick #(recharts.util/custom-y-tick % y-axis-config)
                                              :tickLine false
                                              :axisLine false
                                              :domain [0 y-max]}
                                             (when y-axis-width {:width y-axis-width}))])
           (doall
             (map
               (fn [data-key]
                 (let [gradient-id (str "gradient-" (util/string->hash data-key))
                       {:keys [opacity]} (get key-positions data-key)
                       stack-name (get-stack-name data-key stack-separator)
                       fill-color (if use-gradients
                                    (str "url(#" gradient-id ")")
                                    (get-color-for-key data-key stack-name-colors stack-separator))
                       base-opacity (if use-gradients
                                      (* opacity 0.8)
                                      1)
                       stack-keys (get keys-by-stack stack-name)
                       {:keys [on-click on-double-click]} bar-config
                       on-click (or on-click
                                    (fn [clj-data]
                                      (let [x-name (get-in clj-data [:payload :name])]
                                        (if (and (= @selected-key* data-key)
                                                 (= @selected-datum* x-name))
                                          (do
                                            (reset! selected-key* nil)
                                            (reset! selected-datum* nil))
                                          (do
                                            (reset! selected-key* data-key)
                                            (reset! selected-datum* x-name))))))]
                   (into
                     [recharts.util/re-bar {:key data-key
                                            :dataKey data-key
                                            :stackId stack-name
                                            :fill fill-color
                                            :fillOpacity base-opacity
                                            :stroke (style/alc-color "Background/card_hex")
                                            :strokeWidth 1
                                            :barSize bar-size
                                            :name data-key
                                            :onMouseEnter (fn [data _e]
                                                            (let [clj-data (js->clj data :keywordize-keys true)
                                                                  values (build-stack-values stack-keys
                                                                                             stack-separator
                                                                                             clj-data)]
                                                              (reset! tooltip-state* {:stack-name stack-name
                                                                                      :hovered-key data-key
                                                                                      :values values
                                                                                      :data clj-data})))
                                            :onMouseLeave (fn [_ _]
                                                            (reset! tooltip-state* nil))
                                            :onClick (if (and on-click on-double-click)
                                                       (fn [d index e]
                                                         (let [clj-data (js->clj d :keywordize-keys true)]
                                                           (when @click-timer* (js/clearTimeout @click-timer*))
                                                           (reset! click-timer* (js/setTimeout (fn [] (on-click clj-data
                                                                                                                index
                                                                                                                e))
                                                                                               250))))
                                                       on-click)
                                            :onDoubleClick (when on-double-click
                                                             (fn [d index e]
                                                               (when @click-timer*
                                                                 (js/clearTimeout @click-timer*)
                                                                 (reset! click-timer* nil))
                                                               (on-double-click (js->clj d :keywordize-keys true)
                                                                                index
                                                                                e)))}]
                     (map-indexed
                       (fn [idx point]
                         (let [point-name (:name point)
                               clicked-series (= @selected-key* data-key)
                               clicked? (and clicked-series (= @selected-datum* point-name))
                               fill-opacity (cond
                                              clicked? 1.0
                                              clicked-series 1.0
                                              (some? @selected-key*) 0.1
                                              :else base-opacity)]
                           [recharts.util/re-cell {:key (str (name data-key) "-" idx)
                                                   :fillOpacity fill-opacity}]))
                       chart-data))))
               data-keys))]]
         [custom-stack-tooltip (merge {:tooltip-state* tooltip-state*
                                       :mouse-position* mouse-position*
                                       :stack-name-colors stack-name-colors
                                       :key-positions key-positions
                                       :header-transf-fn tooltip-header-transf-fn
                                       :label-transf-fn tooltip-label-transf-fn
                                       :value-transf-fn tooltip-value-transf-fn
                                       :show-percent? tooltip-show-percent?
                                       :column-template tooltip-column-template}
                                      tooltip-config)]
         ;; Fixed legend outside scrollable area
         (when show-legend
           [recharts.util/standalone-legend
            {:display-fn util/->capitalize-words
             :entries (mapv (fn [stack-name]
                              {:label stack-name
                               :color (get stack-name-colors stack-name)})
                            (distinct (map #(get-stack-name % stack-separator) data-keys)))}])]))))